import { useEffect, useState, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Search, Heart, Settings, MapPin, History,
  LogOut, User, Moon, Sun, Share2, ChevronRight,
  Loader2, X, Trash2, ArrowLeft, Shield, Users,
  Building2, Mail, Send, Check, XCircle, Clock,
  ChevronDown, Save, Plus, Pencil, Fuel
} from "lucide-react";

const PromortekLogo = ({ className = "h-12" }) => {
  const size = className.includes('h-20') || className.includes('h-24') ? 'text-5xl' : className.includes('h-8') ? 'text-xl' : 'text-2xl';
  return (
    <span className={`font-heading font-black tracking-tight text-white ${size} ${className}`} data-testid="promortek-logo">
      PROMORTEK
    </span>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safe error message extractor - prevents React crash from rendering objects
const getErrorMsg = (detail, fallback = 'Request failed') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
  if (typeof detail === 'object') return detail.message || detail.msg || detail.details || JSON.stringify(detail);
  return String(detail);
};

// Safe toast wrappers - NEVER pass objects to sonner (causes React crash)
const _toast = { error: toast.error, success: toast.success, info: toast.info };
const safeToast = {
  error: (msg) => _toast.error(typeof msg === 'string' ? msg : getErrorMsg(msg)),
  success: (msg) => _toast.success(typeof msg === 'string' ? msg : getErrorMsg(msg, 'Success')),
  info: (msg) => _toast.info(typeof msg === 'string' ? msg : getErrorMsg(msg, '')),
};

// Safe JSON parser - prevents "Unexpected token '<'" crash when proxy returns HTML
const safeJson = async (response) => {
  let cloneForText = null;
  try { cloneForText = response.clone(); } catch {}
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try { return await response.json(); }
    catch {
      if (cloneForText) {
        try {
          const txt = await cloneForText.text();
          if (txt) return JSON.parse(txt);
        } catch {}
      }
    }
  }
  let snippet = '';
  try {
    if (cloneForText) snippet = (await cloneForText.text()).slice(0, 80);
    else snippet = (await response.text()).slice(0, 80);
  } catch {}
  if (snippet) {
    try {
      const parsed = JSON.parse(snippet);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {}
  }
  const status = response.status;
  if (status === 502 || status === 503 || status === 504) {
    return { detail: 'Server is temporarily unavailable. Please try again in a moment.' };
  }
  if (status >= 500) {
    return { detail: 'Server error. Please try again or contact support.' };
  }
  if (status === 401 || status === 403) {
    return { detail: 'Authentication failed. Please check your credentials.' };
  }
  if (status === 404) {
    return { detail: 'Requested resource not found.' };
  }
  if (status >= 200 && status < 300) {
    return {};
  }
  return { detail: `Request failed (${status})` };
};


// ============== Theme Context ==============
const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============== Auth Context ==============
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (!user) return;
    const heartbeatInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/auth/heartbeat`, {
          method: 'POST', credentials: 'include'
        });
        if (!response.ok) {
          setUser(null);
          setSessionExpired(true);
          safeToast.error('Session expired due to inactivity. Please log in again.');
        }
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    }, 2 * 60 * 1000);
    return () => clearInterval(heartbeatInterval);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let lastActivity = Date.now();
    const throttledActivityHandler = () => {
      const now = Date.now();
      if (now - lastActivity > 30000) {
        lastActivity = now;
        fetch(`${API}/auth/heartbeat`, { method: 'POST', credentials: 'include' }).catch(() => {});
      }
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, throttledActivityHandler, { passive: true }));
    return () => events.forEach(event => window.removeEventListener(event, throttledActivityHandler));
  }, [user]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setSessionExpired(false);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) { console.error('Logout error:', error); }
    setUser(null);
    sessionStorage.clear();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== Auth Callback ==============
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const processAuth = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      if (!sessionId) { setError('No session ID found'); return; }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Authentication failed');
        const userData = await response.json();
        setUser(userData);

        if (userData.status === 'pending') {
          navigate('/pending', { replace: true });
        } else {
          fetch(`${API}/refresh-fuel-data`, { credentials: 'include' }).catch(() => {});
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        setError(err.message);
        safeToast.error('Authentication failed');
        setTimeout(() => navigate('/'), 3000);
      }
    };
    processAuth();
  }, [location, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive mb-2">Authentication Error</p>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-sm text-muted-foreground mt-2">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-brand-red" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// ============== Pending Approval Page ==============
const PendingApprovalPage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-brand-navy" />
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <PromortekLogo className="h-20 mx-auto mb-6" />
          <div className="bg-white/10 backdrop-blur-sm rounded-md p-8 mb-6">
            <Clock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-3" data-testid="pending-title">
              Account Pending Approval
            </h1>
            <p className="text-slate-300 mb-2">
              Your account has been created and is awaiting approval from an administrator.
            </p>
            <p className="text-slate-400 text-sm">
              You will receive an email notification once your account is approved.
            </p>
          </div>
          <button
            data-testid="pending-logout-btn"
            onClick={handleLogout}
            className="text-slate-400 hover:text-white transition-colors text-sm"
          >
            Sign out and return to login
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== Login Page ==============
const LoginPage = () => {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('login'); // login, register, verify, forgot, reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      if (user.status === 'pending') navigate('/pending');
      else navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }), credentials: 'include'
      });
      const data = await safeJson(response);
      if (!response.ok) throw new Error(getErrorMsg(data.detail, 'Login failed'));
      if (data.status === 'pending') {
        setUser(data);
        navigate('/pending');
      } else {
        setUser(data);
        fetch(`${API}/refresh-fuel-data`, { credentials: 'include' }).catch(() => {});
        navigate('/dashboard');
      }
    } catch (error) { safeToast.error(error.message); }
    finally { setFormLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) { safeToast.error('Password must be at least 6 characters'); return; }
    setFormLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 409 || response.status === 400) {
          safeToast.error('An account with this email already exists');
        } else {
          safeToast.error('Registration failed. Please try again.');
        }
        return;
      }
      const data = await safeJson(response);
      safeToast.success(data.message || 'Check your email for verification code');
      setAuthMode('verify');
    } catch (error) { safeToast.error(error.message || 'Registration failed'); }
    finally { setFormLoading(false); }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const response = await fetch(`${API}/auth/verify-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await safeJson(response);
      if (!response.ok) throw new Error(getErrorMsg(data.detail, 'Verification failed'));
      safeToast.success(data.message);
      setAuthMode('login');
      setCode('');
    } catch (error) { safeToast.error(error.message); }
    finally { setFormLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await safeJson(response);
      if (!response.ok) throw new Error(getErrorMsg(data.detail, 'Failed'));
      safeToast.success('Reset code sent to your email');
      setCode('');
      setNewPassword('');
      setAuthMode('reset');
    } catch (error) { safeToast.error(error.message); }
    finally { setFormLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { safeToast.error('Password must be at least 6 characters'); return; }
    setFormLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });
      const data = await safeJson(response);
      if (!response.ok) throw new Error(getErrorMsg(data.detail, 'Reset failed'));
      safeToast.success('Password reset! You can now login.');
      setAuthMode('login');
      setCode('');
      setNewPassword('');
    } catch (error) { safeToast.error(error.message); }
    finally { setFormLoading(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1684022872335-3472132f37f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxVSyUyMHBldHJvbCUyMHN0YXRpb24lMjBtb2Rlcm58ZW58MHx8fHwxNzcyODA1MDc2fDA&ixlib=rb-4.1.0&q=85')` }} />
      <div className="absolute inset-0 bg-brand-navy/90" />
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <PromortekLogo className="h-20 sm:h-24 mx-auto mb-3" />
            <p className="text-lg text-slate-300 font-body">Price Finder</p>
          </div>

          {authMode === 'login' && (
            <div className="space-y-3" data-testid="login-form">
              <form onSubmit={handleEmailLogin} className="space-y-3" autoComplete="off">
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" required autoComplete="username"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password" required autoComplete="current-password" className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <button data-testid="login-submit-btn" type="submit" disabled={formLoading}
                  className="w-full bg-brand-red text-white font-bold py-3 px-6 rounded-sm flex items-center justify-center gap-2 hover:bg-red-700 transition-all">
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Sign In
                </button>
              </form>
              <button onClick={() => { setCode(''); setNewPassword(''); setAuthMode('forgot'); }} className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </button>
              <p className="text-slate-400 text-sm mt-4">
                Don't have an account? <button onClick={() => setAuthMode('register')} className="text-white hover:underline" data-testid="register-link">Register</button>
              </p>
            </div>
          )}

          {authMode === 'register' && (
            <div data-testid="register-form">
              <form onSubmit={handleRegister} className="space-y-3">
                <input data-testid="register-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Full Name" required className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" required className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (min 6 characters)" required className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <button data-testid="register-submit-btn" type="submit" disabled={formLoading}
                  className="w-full bg-brand-red text-white font-bold py-3 px-6 rounded-sm flex items-center justify-center gap-2 hover:bg-red-700 transition-all">
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Create Account
                </button>
              </form>
              <p className="text-slate-400 text-sm mt-4">
                Already have an account? <button onClick={() => setAuthMode('login')} className="text-white hover:underline">Sign In</button>
              </p>
            </div>
          )}

          {authMode === 'verify' && (
            <div data-testid="verify-form">
              <p className="text-slate-300 mb-4">Enter the 6-digit code sent to <strong className="text-white">{email}</strong></p>
              <form onSubmit={handleVerifyEmail} className="space-y-3">
                <input data-testid="verify-code" type="text" value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter verification code" required maxLength={6}
                  className="w-full bg-white/10 border border-white/20 text-white text-center text-2xl tracking-widest placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <button data-testid="verify-submit-btn" type="submit" disabled={formLoading}
                  className="w-full bg-brand-red text-white font-bold py-3 px-6 rounded-sm flex items-center justify-center gap-2 hover:bg-red-700 transition-all">
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Verify Email
                </button>
              </form>
              <button onClick={() => setAuthMode('login')} className="text-slate-400 hover:text-white text-sm mt-4 transition-colors">Back to login</button>
            </div>
          )}

          {authMode === 'forgot' && (
            <div data-testid="forgot-form">
              <p className="text-slate-300 mb-4">Enter your email to receive a reset code</p>
              <form onSubmit={handleForgotPassword} className="space-y-3">
                <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" required className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <button data-testid="forgot-submit-btn" type="submit" disabled={formLoading}
                  className="w-full bg-brand-red text-white font-bold py-3 px-6 rounded-sm flex items-center justify-center gap-2 hover:bg-red-700 transition-all">
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Send Reset Code
                </button>
              </form>
              <button onClick={() => setAuthMode('login')} className="text-slate-400 hover:text-white text-sm mt-4 transition-colors">Back to login</button>
            </div>
          )}

          {authMode === 'reset' && (
            <div data-testid="reset-form">
              <p className="text-slate-300 mb-4">Enter the code sent to <strong className="text-white">{email}</strong></p>
              <form onSubmit={handleResetPassword} className="space-y-3" autoComplete="off">
                <input data-testid="reset-code" type="text" value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Reset code" required maxLength={6} autoComplete="off"
                  className="w-full bg-white/10 border border-white/20 text-white text-center text-2xl tracking-widest placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <input data-testid="reset-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)" required autoComplete="new-password"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-sm px-4 py-3 focus:outline-none focus:border-white/50" />
                <button data-testid="reset-submit-btn" type="submit" disabled={formLoading}
                  className="w-full bg-brand-red text-white font-bold py-3 px-6 rounded-sm flex items-center justify-center gap-2 hover:bg-red-700 transition-all">
                  {formLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Reset Password
                </button>
              </form>
              <button onClick={() => setAuthMode('login')} className="text-slate-400 hover:text-white text-sm mt-4 transition-colors">Back to login</button>
            </div>
          )}

          <p className="text-slate-500 text-sm mt-6">Powered by UK Government Fuel Price Finder Data</p>
        </div>
      </div>
    </div>
  );
};

// ============== Protected Route ==============
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) navigate('/');
    if (!loading && user && user.status === 'pending') navigate('/pending');
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
      </div>
    );
  }

  if (!user && !location.state?.user) return null;

  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.includes(user?.role)) {
      return (
        <Layout>
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </Layout>
      );
    }
  }

  return children;
};

// ============== Layout ==============
const Layout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/dashboard', icon: Search, label: 'Search' },
    ...(user?.role === 'manager' || user?.role === 'admin'
      ? [{ path: '/submit-prices', icon: Send, label: 'Submit' }]
      : []),
    { path: '/favourites', icon: Heart, label: 'Favourites' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <header className="sticky top-0 z-40 bg-brand-navy text-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <PromortekLogo className="h-8" />
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <nav className="hidden md:flex items-center gap-1 ml-4">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    location.pathname === item.path ? 'bg-white/20' : 'hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            {user && (
              <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate('/'); }}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
        {title && (
          <div className="bg-brand-navy/80 border-t border-white/10">
            <div className="max-w-5xl mx-auto px-4 py-2">
              <h1 className="font-heading text-lg font-semibold uppercase tracking-wide">{title}</h1>
            </div>
          </div>
        )}
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6">{children}</main>
      <nav className="bottom-nav" data-testid="bottom-nav">
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <Toaster position="top-center" richColors toastOptions={{ style: { zIndex: 99999 } }} />
    </div>
  );
};

// ============== Dashboard Page ==============
const DashboardPage = () => {
  const [postcode, setPostcode] = useState(() => sessionStorage.getItem('lastPostcode') || '');
  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState(() => {
    const saved = sessionStorage.getItem('lastSearchResults');
    return saved ? JSON.parse(saved) : null;
  });
  const [recentSearches, setRecentSearches] = useState([]);
  const [resultLimit, setResultLimit] = useState(() => {
    const saved = sessionStorage.getItem('resultLimit');
    return saved ? parseInt(saved, 10) : 20;
  });
  const navigate = useNavigate();

  useEffect(() => { fetchRecentSearches(); }, []);

  const fetchRecentSearches = async () => {
    try {
      const response = await fetch(`${API}/recent-searches`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRecentSearches(data.searches || []);
      }
    } catch (error) { console.error('Error fetching recent searches:', error); }
  };

  const handleSearch = async (searchPostcode, limit) => {
    const pc = searchPostcode || postcode;
    if (!pc.trim()) { safeToast.error('Please enter a postcode'); return; }
    const useLimit = limit || resultLimit;
    setLoading(true);
    try {
      const response = await fetch(`${API}/stations?postcode=${encodeURIComponent(pc)}&limit=${useLimit}`, { credentials: 'include' });
      if (!response.ok) {
        let errorMsg = 'Search failed';
        try { const error = await response.json(); errorMsg = getErrorMsg(error.detail, errorMsg); } catch (e) {}
        throw new Error(errorMsg);
      }
      const data = await response.json();
      setStations(data);
      sessionStorage.setItem('lastSearchResults', JSON.stringify(data));
      sessionStorage.setItem('lastPostcode', pc);
      fetchRecentSearches();
    } catch (error) {
      safeToast.error(error.message || 'Failed to search stations');
    } finally { setLoading(false); }
  };

  const handleLimitChange = (newLimit) => {
    setResultLimit(newLimit);
    sessionStorage.setItem('resultLimit', String(newLimit));
    // Re-search with new limit if we already have results
    if (stations?.postcode) {
      handleSearch(stations.postcode, newLimit);
    }
  };

  const clearSearch = () => {
    setStations(null);
    setPostcode('');
    sessionStorage.removeItem('lastSearchResults');
    sessionStorage.removeItem('lastPostcode');
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            data-testid="postcode-input"
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Enter UK postcode (e.g. SW1A 1AA)"
            className="search-input flex-1"
          />
          <select
            data-testid="result-limit-select"
            value={resultLimit}
            onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
            className="search-input w-auto px-3 text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <button
            data-testid="search-btn"
            onClick={() => handleSearch()}
            disabled={loading}
            className="btn-primary px-6"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {!stations && recentSearches.length > 0 && (
        <div className="mb-6 animate-in">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Recent Searches</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentSearches.map((search) => (
              <button
                key={search.search_id}
                data-testid={`recent-${search.postcode}`}
                onClick={() => { setPostcode(search.postcode); handleSearch(search.postcode); }}
                className="px-3 py-2 bg-card border border-border rounded-sm text-sm hover:border-brand-red transition-colors"
              >
                {search.postcode}
              </button>
            ))}
          </div>
        </div>
      )}

      {stations && (
        <div className="animate-in">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Showing {stations.total} stations near</p>
              <p className="font-heading text-xl font-bold uppercase">{stations.postcode}</p>
            </div>
            <button data-testid="clear-search-btn" onClick={clearSearch} className="p-2 hover:bg-muted rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            {stations.stations.map((station, index) => (
              <StationCard key={station.station_id} station={station} index={index} onClick={() => navigate(`/station/${station.station_id}`)} />
            ))}
          </div>
        </div>
      )}

      {!stations && recentSearches.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            <MapPin className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="font-heading text-xl font-bold uppercase mb-2">Find Fuel Near You</h2>
          <p className="text-muted-foreground max-w-sm mx-auto">
            Enter a UK postcode above to discover the cheapest fuel stations in your area.
          </p>
        </div>
      )}
    </Layout>
  );
};

// ============== Station Card ==============
const StationCard = ({ station, index, onClick }) => {
  const [isFav, setIsFav] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/favourites`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.favourites?.some(f => f.station.station_id === station.station_id)) setIsFav(true);
      }).catch(() => {});
  }, [station.station_id]);

  const toggleFavourite = async (e) => {
    e.stopPropagation();
    setFavLoading(true);
    try {
      if (isFav) {
        const res = await fetch(`${API}/favourites/${station.station_id}`, { method: 'DELETE', credentials: 'include' });
        if (res.ok) { setIsFav(false); safeToast.success('Removed from favourites'); }
      } else {
        const res = await fetch(`${API}/favourites`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ station_id: station.station_id }), credentials: 'include' });
        if (res.ok) { setIsFav(true); safeToast.success('Added to favourites'); }
      }
    } catch { safeToast.error('Failed to update favourite'); }
    finally { setFavLoading(false); }
  };

  const getUpdateTime = () => {
    const ts = station.price_submission_timestamp;
    if (!ts) return null;
    const latest = ts.E10 || ts.E5 || ts.B7S || ts.B7P;
    if (!latest) return null;
    try {
      const d = new Date(latest);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };
  const updateTime = getUpdateTime();

  return (
    <div
      data-testid={`station-card-${index}`}
      className="station-card slide-in"
      style={{ animationDelay: `${index * 50}ms` }}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-lg font-bold uppercase truncate">{station.trading_name}</h3>
            <button
              data-testid={`fav-btn-${index}`}
              onClick={toggleFavourite}
              disabled={favLoading}
              className="flex-shrink-0 p-1 rounded-full hover:bg-muted transition-colors"
            >
              <Heart className={`w-4 h-4 ${isFav ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
            </button>
          </div>
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span>{station.location?.postcode || 'N/A'}</span>
            <span className="mx-1">·</span>
            <span>{station.distance} miles</span>
          </div>
          {station.fuel_price && (
            <div className="flex flex-wrap gap-2 mt-2">
              {station.fuel_price.E10 && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">E10: {station.fuel_price.E10}p</span>}
              {station.fuel_price.E5 && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">E5: {station.fuel_price.E5}p</span>}
              {station.fuel_price.B7S && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">B7: {station.fuel_price.B7S}p</span>}
              {station.fuel_price.B7P && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">B7P: {station.fuel_price.B7P}p</span>}
            </div>
          )}
          {updateTime && <div className="text-xs text-muted-foreground mt-1">Updated: {updateTime}</div>}
        </div>
        <div className="flex items-center"><ChevronRight className="w-5 h-5 text-muted-foreground" /></div>
      </div>
    </div>
  );
};

// ============== Station Detail Page ==============
const StationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavourite, setIsFavourite] = useState(false);

  useEffect(() => { fetchStation(); }, [id]);

  const fetchStation = async () => {
    try {
      const response = await fetch(`${API}/station/${id}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Station not found');
      const data = await response.json();
      setStation(data);
      setIsFavourite(data.is_favourite);
    } catch (error) {
      safeToast.error('Failed to load station');
      navigate('/dashboard');
    } finally { setLoading(false); }
  };

  const toggleFavourite = async () => {
    try {
      if (isFavourite) {
        await fetch(`${API}/favourites/${id}`, { method: 'DELETE', credentials: 'include' });
        setIsFavourite(false);
        safeToast.success('Removed from favourites');
      } else {
        await fetch(`${API}/favourites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ station_id: id }),
          credentials: 'include'
        });
        setIsFavourite(true);
        safeToast.success('Added to favourites');
      }
    } catch (error) { safeToast.error('Failed to update favourites'); }
  };

  const handleShare = async () => {
    if (!station) return;
    const priceLines = [];
    if (station.fuel_price?.E10) priceLines.push(`E10: ${station.fuel_price.E10}p`);
    if (station.fuel_price?.E5) priceLines.push(`E5: ${station.fuel_price.E5}p`);
    if (station.fuel_price?.B7S) priceLines.push(`Diesel (B7): ${station.fuel_price.B7S}p`);
    if (station.fuel_price?.B7P) priceLines.push(`Premium Diesel: ${station.fuel_price.B7P}p`);

    const shareText = `Fuel prices at ${station.trading_name}\n${station.location?.city || ''} ${station.location?.postcode || ''}\nBrand: ${station.brand_name || 'Independent'}\n\n${priceLines.join('\n')}\n\nFound via PROMORTEK Price Finder`;
    if (navigator.share) {
      try { await navigator.share({ title: `Fuel Prices - ${station.trading_name}`, text: shareText }); }
      catch (error) { if (error.name !== 'AbortError') navigator.clipboard.writeText(shareText).then(() => safeToast.success('Copied to clipboard')); }
    } else {
      navigator.clipboard.writeText(shareText);
      safeToast.success('Copied to clipboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return 'N/A'; }
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div></Layout>;
  if (!station) return null;

  return (
    <Layout title={station.trading_name}>
      <div className="space-y-6 animate-in">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm font-medium">Back to results</span>
        </button>

        <div className="bg-card border border-border rounded-md p-6" data-testid="station-details">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Site Details</h2>
          <div className="space-y-3">
            <div><p className="text-sm text-muted-foreground">Site Name</p><p className="font-heading text-xl font-bold uppercase">{station.trading_name}</p></div>
            {station.brand_name && <div><p className="text-sm text-muted-foreground">Brand</p><p className="font-semibold">{station.brand_name}</p></div>}
            <div><p className="text-sm text-muted-foreground">Location</p><p className="font-semibold">{station.location?.address && `${station.location.address}, `}{station.location?.city || 'Unknown'}</p></div>
            <div><p className="text-sm text-muted-foreground">Postcode</p><p className="font-semibold">{station.location?.postcode || 'N/A'}</p></div>
            {station.opening_hours && <div><p className="text-sm text-muted-foreground">Opening Hours</p><p className="font-semibold">{station.opening_hours}</p></div>}
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-6" data-testid="fuel-prices">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Fuel Prices</h2>
          <div className="grid gap-4">
            {station.fuel_price?.E10 && <FuelPriceRow label="E10 (Unleaded)" badge="fuel-badge-e10" price={station.fuel_price.E10} timestamp={station.price_submission_timestamp?.E10} formatDate={formatDate} />}
            {station.fuel_price?.E5 && <FuelPriceRow label="E5 (Super Unleaded)" badge="fuel-badge-e5" price={station.fuel_price.E5} timestamp={station.price_submission_timestamp?.E5} formatDate={formatDate} />}
            {station.fuel_price?.B7S && <FuelPriceRow label="B7 (Standard Diesel)" badge="fuel-badge-b7s" price={station.fuel_price.B7S} timestamp={station.price_submission_timestamp?.B7S} formatDate={formatDate} />}
            {station.fuel_price?.B7P && <FuelPriceRow label="B7 Premium (Diesel)" badge="fuel-badge-b7p" price={station.fuel_price.B7P} timestamp={station.price_submission_timestamp?.B7P} formatDate={formatDate} />}
          </div>
        </div>

        <div className="flex gap-3">
          <button data-testid="favourite-btn" onClick={toggleFavourite}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-sm font-bold uppercase tracking-wider transition-all ${isFavourite ? 'bg-brand-red text-white' : 'border-2 border-border hover:border-brand-red'}`}>
            <Heart className={`w-5 h-5 ${isFavourite ? 'fill-current' : ''}`} />{isFavourite ? 'Saved' : 'Save'}
          </button>
          <button data-testid="share-btn" onClick={handleShare}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-sm font-bold uppercase tracking-wider border-2 border-border hover:border-brand-navy transition-all">
            <Share2 className="w-5 h-5" />Share
          </button>
        </div>
      </div>
    </Layout>
  );
};

const FuelPriceRow = ({ label, badge, price, timestamp, formatDate }) => (
  <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
    <div>
      <span className={`fuel-badge ${badge} mb-1`}>{label}</span>
      <p className="text-xs text-muted-foreground mt-1">Updated: {formatDate(timestamp)}</p>
    </div>
    <p className="price-tag">{price}p</p>
  </div>
);

// ============== Favourites Page ==============
const FavouritesPage = () => {
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchFavourites(true); }, []);

  const fetchFavourites = async (autoRefresh = false) => {
    try {
      // Trigger background data refresh when page is viewed
      if (autoRefresh) {
        setRefreshing(true);
        fetch(`${API}/refresh-fuel-data`, { credentials: 'include' }).catch(() => {});
      }
      const response = await fetch(`${API}/favourites`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFavourites(data.favourites || []);
      }
    } catch (error) { safeToast.error('Failed to load favourites'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const removeFavourite = async (stationId) => {
    try {
      await fetch(`${API}/favourites/${stationId}`, { method: 'DELETE', credentials: 'include' });
      setFavourites(prev => prev.filter(f => f.station.station_id !== stationId));
      safeToast.success('Removed from favourites');
    } catch (error) { safeToast.error('Failed to remove favourite'); }
  };

  const getUpdateTime = (station) => {
    const ts = station.price_submission_timestamp;
    if (!ts) return null;
    const latest = ts.E10 || ts.E5 || ts.B7S || ts.B7P;
    if (!latest) return null;
    try {
      const d = new Date(latest);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };

  if (loading) return <Layout title="Favourites"><div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div></Layout>;

  return (
    <Layout title="Favourites">
      {favourites.length === 0 ? (
        <div className="text-center py-12" data-testid="no-favourites">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4"><Heart className="w-8 h-8 text-muted-foreground" /></div>
          <h2 className="font-heading text-xl font-bold uppercase mb-2">No Favourites Yet</h2>
          <p className="text-muted-foreground max-w-sm mx-auto mb-4">Save your favourite fuel stations for quick access.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary" data-testid="search-stations-btn">Search Stations</button>
        </div>
      ) : (
        <div className="animate-in">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">{favourites.length} saved station{favourites.length !== 1 ? 's' : ''}</p>
            <button
              data-testid="refresh-favourites-btn"
              onClick={() => fetchFavourites(true)}
              disabled={refreshing}
              className="px-3 py-1.5 border border-border rounded-sm text-sm hover:bg-muted transition-colors flex items-center gap-1"
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              Refresh Prices
            </button>
          </div>
          <div className="space-y-3">
            {favourites.map((fav, index) => {
              const station = fav.station;
              const updateTime = getUpdateTime(station);
              return (
                <div key={fav.favourite_id} data-testid={`favourite-card-${index}`} className="station-card slide-in relative" style={{ animationDelay: `${index * 50}ms` }}>
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/station/${station.station_id}`)}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-bold uppercase truncate">{station.trading_name}</h3>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span>{station.location?.postcode || 'N/A'}</span>
                          {station.brand_name && <><span className="mx-1">·</span><span>{station.brand_name}</span></>}
                        </div>
                        {station.fuel_price && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {station.fuel_price.E10 && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">E10: {station.fuel_price.E10}p</span>}
                            {station.fuel_price.E5 && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">E5: {station.fuel_price.E5}p</span>}
                            {station.fuel_price.B7S && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">B7: {station.fuel_price.B7S}p</span>}
                            {station.fuel_price.B7P && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">B7P: {station.fuel_price.B7P}p</span>}
                          </div>
                        )}
                        {updateTime && <div className="text-xs text-muted-foreground mt-1">Updated: {updateTime}</div>}
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground mt-1" />
                    </div>
                  </div>
                  <button data-testid={`remove-favourite-${index}`} onClick={(e) => { e.stopPropagation(); removeFavourite(station.station_id); }}
                    className="absolute top-2 right-2 p-2 text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Layout>
  );
};

// ============== Submit Prices Page ==============
const SubmitPricesPage = () => {
  const { user } = useAuth();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [prices, setPrices] = useState({});
  const [currentPrices, setCurrentPrices] = useState({});
  const [selectedGrades, setSelectedGrades] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { fetchSite(); fetchHistory(); }, []);

  const fetchSite = async () => {
    try {
      const response = await fetch(`${API}/prices/my-site`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSite(data.site);
        const initial = {};
        const selected = {};
        (data.site.fuel_grades || []).forEach(g => { initial[g] = ''; selected[g] = false; });
        setPrices(initial);
        setSelectedGrades(selected);
        // Fetch current prices after getting site info
        fetchCurrentPrices();
      }
    } catch (error) { console.error('Error fetching site:', error); }
    finally { setLoading(false); }
  };

  const fetchCurrentPrices = async () => {
    setLoadingPrices(true);
    try {
      const response = await fetch(`${API}/prices/current`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const prices = data.prices || {};
        setCurrentPrices(prices);
        // If no prices found, trigger background cache refresh and retry
        if (Object.keys(prices).length === 0 || data.source === 'unavailable') {
          fetch(`${API}/refresh-fuel-data`, { credentials: 'include' })
            .then(res => res.ok && res.json())
            .then(() => {
              // Retry fetching prices after cache refresh
              return fetch(`${API}/prices/current`, { credentials: 'include' });
            })
            .then(res => res.ok && res.json())
            .then(data => {
              if (data && data.prices) setCurrentPrices(data.prices);
            })
            .catch(() => {})
            .finally(() => setLoadingPrices(false));
          return; // Don't set loadingPrices to false yet
        }
      }
    } catch (error) { console.error('Error fetching current prices:', error); }
    finally { setLoadingPrices(false); }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API}/prices/history`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.submissions || []);
      }
    } catch (error) { console.error('Error fetching history:', error); }
  };

  const validatePrice = (value) => {
    if (value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return 'Enter a valid number';
    const minPrice = site?.price_min ?? 100;
    const maxPrice = site?.price_max ?? 250;
    if (num < minPrice || num > maxPrice) return `Must be between ${minPrice} and ${maxPrice}p`;
    const endDigit = site?.price_ending_digit ?? 9;
    const parts = value.split('.');
    if (parts.length > 1 && parts[1].length > 1) return 'Max 1 decimal place';
    if (parts.length === 1 && endDigit !== 0) return `Price must end in .${endDigit}`;
    if (parts.length === 2) {
      const decimal = parseInt(parts[1], 10);
      if (decimal !== endDigit) return `Price must end in .${endDigit} (e.g. ${Math.floor(num)}.${endDigit})`;
    }
    return null;
  };

  const toggleGrade = (grade) => {
    setSelectedGrades(prev => ({ ...prev, [grade]: !prev[grade] }));
  };

  const handleSubmit = async () => {
    // Only submit selected grades
    const priceEntries = Object.entries(prices)
      .filter(([grade, value]) => selectedGrades[grade] && value !== '' && !isNaN(parseFloat(value)))
      .map(([fuel_type, price]) => ({ fuel_type, price: parseFloat(price) }));

    if (priceEntries.length === 0) { safeToast.error('Select at least one grade and enter a price'); return; }

    // Validate selected prices
    for (const [grade, value] of Object.entries(prices)) {
      if (!selectedGrades[grade] || value === '') continue;
      const error = validatePrice(value);
      if (error) { safeToast.error(`${gradeLabels[grade] || grade}: ${error}`); return; }
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/prices/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prices: priceEntries }),
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        if (data.status === 'submitted') {
          safeToast.success(data.message || 'Prices submitted successfully');
        } else {
          safeToast.error(data.message || 'Price submission failed at Fuel Finder API');
        }
        fetchHistory();
        fetchCurrentPrices();
        const reset = {};
        const resetSelected = {};
        Object.keys(prices).forEach(k => { reset[k] = ''; resetSelected[k] = false; });
        setPrices(reset);
        setSelectedGrades(resetSelected);
      } else {
        const errMsg = getErrorMsg(data.detail, 'Submission failed');
        safeToast.error(errMsg);
      }
    } catch (error) { safeToast.error('Failed to submit prices'); }
    finally { setSubmitting(false); }
  };

  const gradeLabels = {
    'E10': 'E10 (Unleaded)', 'E5': 'E5 (Super Unleaded)',
    'B7_Standard': 'B7 (Standard Diesel)', 'B7_Premium': 'B7 (Premium Diesel)',
    'B7_STANDARD': 'B7 (Standard Diesel)', 'B7_PREMIUM': 'B7 (Premium Diesel)',
    'B10': 'B10 (Diesel)', 'HVO': 'HVO (Hydrotreated Vegetable Oil)',
    'LPG': 'LPG', 'ADBLUE': 'AdBlue', 'HYDROGEN': 'Hydrogen'
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try { return new Date(dateString).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return 'N/A'; }
  };

  if (loading) return <Layout title="Submit Prices"><div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div></Layout>;

  if (!site) {
    return (
      <Layout title="Submit Prices">
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold uppercase mb-2">No Site Assigned</h2>
          <p className="text-muted-foreground">Contact your administrator to assign a site to your account.</p>
        </div>
      </Layout>
    );
  }

  const hasSelectedGrades = Object.values(selectedGrades).some(v => v);

  return (
    <Layout title="Submit Prices">
      <div className="space-y-6 animate-in">
        <div className="bg-card border border-border rounded-md p-6" data-testid="site-info">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your Site</h2>
              <p className="font-heading text-xl font-bold">{site.api_site_name || site.site_name}</p>
              {site.brand_name && <p className="text-sm font-medium text-muted-foreground">{site.brand_name}</p>}
              {(site.address || site.city || site.postcode) && (
                <p className="text-sm text-muted-foreground mt-1">
                  {[site.address, site.city, site.postcode].filter(Boolean).join(', ')}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">Site ID: {site.api_site_id || site.node_id}</p>
            </div>
            <button onClick={fetchCurrentPrices} disabled={loadingPrices}
              data-testid="refresh-prices-btn"
              className="px-3 py-2 border border-border rounded-sm text-sm hover:bg-muted transition-colors flex items-center gap-1">
              {loadingPrices ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Refresh Prices
            </button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-6" data-testid="price-form">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">Update Fuel Prices</h2>
          <p className="text-sm text-muted-foreground mb-4">Select the grades you want to update, enter new prices in pence.</p>

          <div className="space-y-3">
            {(site.fuel_grades || []).map(grade => {
              const current = currentPrices[grade];
              const error = selectedGrades[grade] ? validatePrice(prices[grade] || '') : null;
              const isSelected = selectedGrades[grade];
              return (
                <div key={grade} className={`p-4 border rounded-md transition-colors ${isSelected ? 'border-brand-red bg-brand-red/5' : 'border-border'}`}
                  data-testid={`grade-row-${grade}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleGrade(grade)}
                      className="w-5 h-5 accent-brand-red cursor-pointer"
                      data-testid={`grade-select-${grade}`}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{gradeLabels[grade] || grade}</p>
                      {current ? (
                        <p className="text-sm text-muted-foreground">
                          Current: <span className="font-semibold text-foreground">{current.price}p</span>
                          <span className="ml-2 text-xs">Updated: {formatDate(current.last_updated)}</span>
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {loadingPrices ? 'Fetching current prices from Fuel Finder API...' : 'No current price available'}
                        </p>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="ml-8 mt-2">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-muted-foreground w-20">New Price:</label>
                        <input
                          data-testid={`price-input-${grade}`}
                          type="number"
                          step="0.1"
                          min="50"
                          max="399.9"
                          placeholder={current ? `Current: ${current.price}` : 'e.g. 149.9'}
                          value={prices[grade] || ''}
                          onChange={(e) => setPrices(prev => ({ ...prev, [grade]: e.target.value }))}
                          className={`search-input flex-1 ${error ? 'border-red-500' : ''}`}
                        />
                        <span className="text-sm text-muted-foreground">p/litre</span>
                      </div>
                      {error && <p className="text-xs text-red-500 mt-1 ml-24">{error}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            data-testid="submit-prices-btn"
            onClick={handleSubmit}
            disabled={submitting || !hasSelectedGrades}
            className="btn-primary w-full mt-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            Submit Selected Prices
          </button>
        </div>

        {history.length > 0 && (
          <div className="bg-card border border-border rounded-md p-6" data-testid="submission-history">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Recent Submissions</h2>
            <div className="space-y-3">
              {history.slice(0, 10).map((sub) => (
                <div key={sub.submission_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-medium">
                      {sub.prices?.map(p => `${p.fuel_type}: ${p.price}p`).join(', ')}
                    </p>
                    <p className="text-xs text-muted-foreground">{new Date(sub.created_at).toLocaleString('en-GB')}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    sub.status === 'submitted' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                    sub.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                    {typeof sub.status === 'string' ? sub.status : 'unknown'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// ============== Settings Page ==============
const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [adminTab, setAdminTab] = useState('users');
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.newPass) { safeToast.error('Fill in all password fields'); return; }
    if (passwordForm.newPass.length < 6) { safeToast.error('New password must be at least 6 characters'); return; }
    if (passwordForm.newPass !== passwordForm.confirm) { safeToast.error('New passwords do not match'); return; }
    setChangingPassword(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: passwordForm.current, new_password: passwordForm.newPass }),
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        safeToast.success('Password changed successfully');
        setPasswordForm({ current: '', newPass: '', confirm: '' });
      } else { safeToast.error(getErrorMsg(data.detail, 'Failed to change password')); }
    } catch (error) { safeToast.error('Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  const clearRecentSearches = async () => {
    try {
      await fetch(`${API}/recent-searches`, { method: 'DELETE', credentials: 'include' });
      safeToast.success('Recent searches cleared');
    } catch (error) { safeToast.error('Failed to clear recent searches'); }
  };

  return (
    <Layout title="Settings">
      <div className="space-y-6 animate-in">
        {user && (
          <div className="bg-card border border-border rounded-md p-6" data-testid="profile-section">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Profile</h2>
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center"><User className="w-8 h-8 text-muted-foreground" /></div>
              )}
              <div>
                <p className="font-heading text-xl font-bold">{user.name}</p>
                <p className="text-muted-foreground">{user.email}</p>
                <span className="text-xs px-2 py-1 rounded bg-brand-navy text-white uppercase mt-1 inline-block">{user.role}</span>
              </div>
            </div>
          </div>
        )}

        {/* Change Password */}
        {user && !user.picture && (
          <div className="bg-card border border-border rounded-md p-6" data-testid="change-password-section">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Change Password</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Current Password</label>
                <input data-testid="current-password-input" type="password" value={passwordForm.current}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                  autoComplete="current-password" className="search-input w-full" placeholder="Enter current password" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">New Password</label>
                <input data-testid="new-password-input" type="password" value={passwordForm.newPass}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPass: e.target.value }))}
                  autoComplete="new-password" className="search-input w-full" placeholder="Enter new password (min 6 chars)" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Confirm New Password</label>
                <input data-testid="confirm-password-input" type="password" value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                  autoComplete="new-password" className="search-input w-full" placeholder="Confirm new password" />
              </div>
              <button data-testid="change-password-btn" onClick={handleChangePassword} disabled={changingPassword}
                className="px-6 py-2 bg-brand-navy text-white rounded-sm hover:bg-brand-navy/80 disabled:opacity-50">
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-md p-6" data-testid="appearance-section">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Appearance</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              <div><p className="font-semibold">Dark Mode</p><p className="text-sm text-muted-foreground">{theme === 'dark' ? 'Enabled' : 'Disabled'}</p></div>
            </div>
            <button data-testid="toggle-dark-mode" onClick={toggleTheme}
              className={`w-12 h-6 rounded-full transition-colors relative ${theme === 'dark' ? 'bg-brand-red' : 'bg-muted'}`}>
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-6" data-testid="data-section">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Data</h2>
          <button data-testid="clear-searches-btn" onClick={clearRecentSearches}
            className="w-full flex items-center justify-between py-3 border-b border-border hover:text-brand-red transition-colors">
            <div className="flex items-center gap-3"><History className="w-5 h-5" /><span>Clear Recent Searches</span></div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Admin Dashboard */}
        {user?.role === 'admin' && (
          <div className="bg-card border-2 border-brand-red/30 rounded-md overflow-hidden" data-testid="admin-dashboard">
            <div className="bg-brand-red/10 px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-red" />
                <h2 className="font-heading text-lg font-bold uppercase">Admin Dashboard</h2>
              </div>
            </div>
            <div className="flex border-b border-border overflow-x-auto">
              {[
                { id: 'users', label: 'Users', icon: Users },
                { id: 'sites', label: 'Sites', icon: Building2 },
                { id: 'email', label: 'Email', icon: Mail },
              ].map(tab => (
                <button
                  key={tab.id}
                  data-testid={`admin-tab-${tab.id}`}
                  onClick={() => setAdminTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    adminTab === tab.id ? 'border-brand-red text-brand-red' : 'border-transparent hover:text-brand-red'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />{tab.label}
                </button>
              ))}
            </div>
            <div className="p-6">
              {adminTab === 'users' && <AdminUsersPanel />}
              {adminTab === 'sites' && <AdminSitesPanel />}
              {adminTab === 'email' && <AdminEmailPanel />}
            </div>
          </div>
        )}

        <button data-testid="settings-logout-btn" onClick={handleLogout}
          className="w-full bg-destructive/10 text-destructive hover:bg-destructive/20 rounded-md p-4 flex items-center justify-center gap-2 font-bold uppercase tracking-wider transition-colors">
          <LogOut className="w-5 h-5" />Sign Out
        </button>

        <div className="text-center text-sm text-muted-foreground">
          <p>PROMORTEK Price Finder</p>
          <p>Powered by UK Government Fuel Price Finder Data</p>
        </div>
      </div>
    </Layout>
  );
};

// ============== Admin Users Panel ==============
const AdminUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);

  useEffect(() => { fetchUsers(); fetchSites(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setUsers(data.users || []); }
    } catch (error) { safeToast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/admin/sites`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setSites(data.sites || []); }
    } catch (error) { console.error('Error loading sites'); }
  };

  const approveUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/approve`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { safeToast.success('User approved'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to approve user'); }
  };

  const rejectUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/reject`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { safeToast.success('User rejected'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to reject user'); }
  };

  const updateRole = async (userId, role) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }), credentials: 'include'
      });
      if (response.ok) { safeToast.success('Role updated'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to update role'); }
  };

  const assignSite = async (userId, siteId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/assign-site`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: siteId || null }), credentials: 'include'
      });
      if (response.ok) { safeToast.success('Site assigned'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to assign site'); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? An email notification will be sent.`)) return;
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { safeToast.success('User deleted'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to delete user'); }
  };

  const disableUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/disable`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { safeToast.success('User disabled'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to disable user'); }
  };

  const enableUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/enable`, { method: 'PUT', credentials: 'include' });
      if (response.ok) { safeToast.success('User enabled'); fetchUsers(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail)); }
    } catch (error) { safeToast.error('Failed to enable user'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const pendingUsers = users.filter(u => u.status === 'pending');
  const allUsers = users;

  return (
    <div className="space-y-6">
      {pendingUsers.length > 0 && (
        <div>
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-yellow-600 dark:text-yellow-400 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Pending Approval ({pendingUsers.length})
          </h3>
          <div className="space-y-2">
            {pendingUsers.map(u => (
              <div key={`${u.user_id}_${u.email}`} data-testid={`pending-user-${u.user_id}`}
                className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <div>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveUser(u.user_id)} data-testid={`approve-${u.user_id}`}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Approve
                  </button>
                  <button onClick={() => rejectUser(u.user_id)} data-testid={`reject-${u.user_id}`}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          All Users ({allUsers.length})
        </h3>
        <div className="space-y-2">
          {allUsers.map(u => (
            <div key={`${u.user_id}_${u.email}`} data-testid={`user-row-${u.user_id}`}
              className="p-3 border border-border rounded-md">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {u.status === 'approved' ? (
                    <>
                      <span className="text-xs px-2 py-1 rounded uppercase bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Approved</span>
                      {u.email.toLowerCase() !== 'vijay@promortek.com' && (
                        <button onClick={() => disableUser(u.user_id)} data-testid={`disable-${u.user_id}`}
                          className="px-2 py-1 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700">
                          Disable
                        </button>
                      )}
                    </>
                  ) : u.status === 'disabled' ? (
                    <>
                      <span className="text-xs px-2 py-1 rounded uppercase bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">Disabled</span>
                      <button onClick={() => enableUser(u.user_id)} data-testid={`enable-${u.user_id}`}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Enable
                      </button>
                    </>
                  ) : u.status === 'rejected' ? (
                    <>
                      <span className="text-xs px-2 py-1 rounded uppercase bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">Rejected</span>
                      <button onClick={() => approveUser(u.user_id)} data-testid={`approve-active-${u.user_id}`}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Approve
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs px-2 py-1 rounded uppercase bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">Pending</span>
                      <button onClick={() => approveUser(u.user_id)} data-testid={`approve-active-${u.user_id}`}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Approve
                      </button>
                      <button onClick={() => rejectUser(u.user_id)} data-testid={`reject-active-${u.user_id}`}
                        className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  data-testid={`role-select-${u.user_id}`}
                  value={u.role || 'user'}
                  onChange={(e) => updateRole(u.user_id, e.target.value)}
                  className="text-sm border border-border rounded px-2 py-1 bg-background"
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <select
                  data-testid={`site-select-${u.user_id}`}
                  value={u.assigned_site_id || ''}
                  onChange={(e) => assignSite(u.user_id, e.target.value)}
                  className="text-sm border border-border rounded px-2 py-1 bg-background"
                >
                  <option value="">No site assigned</option>
                  {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.site_name}</option>)}
                </select>
                {u.email.toLowerCase() !== 'vijay@promortek.com' && (
                  <button onClick={() => deleteUser(u.user_id, u.email)} data-testid={`delete-${u.user_id}`}
                    className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== Admin Sites Panel ==============
const AdminSitesPanel = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [availableGrades, setAvailableGrades] = useState([]);
  const [fetchingGrades, setFetchingGrades] = useState(false);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [apiGradesFetched, setApiGradesFetched] = useState(false);
  const [form, setForm] = useState({ site_name: '', node_id: '', api_site_id: '', api_client_id: '', api_client_secret: '', fuel_grades: [], site_email: '', price_ending_digit: '9', price_min: '100', price_max: '250' });
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyingSiteEmail, setVerifyingSiteEmail] = useState(null);

  useEffect(() => { fetchSites(); fetchGrades(); }, []);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/admin/sites`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setSites(data.sites || []); }
    } catch (error) { safeToast.error('Failed to load sites'); }
    finally { setLoading(false); }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(`${API}/admin/fuel-grades`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setAvailableGrades(data.grades || []); }
    } catch (error) { console.error('Error loading grades'); }
  };

  const fetchGradesFromAPI = async () => {
    if (!form.node_id) { safeToast.error('Enter Node ID first'); return; }
    if (!form.api_client_id || !form.api_client_secret) { safeToast.error('Enter API credentials first'); return; }
    setFetchingGrades(true);
    try {
      const response = await fetch(`${API}/admin/sites/fetch-grades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node_id: form.node_id, api_client_id: form.api_client_id, api_client_secret: form.api_client_secret }),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.grades && data.grades.length > 0) {
          setForm(prev => ({ ...prev, fuel_grades: data.grades }));
          setApiGradesFetched(true);
          safeToast.success(`Found ${data.grades.length} fuel grades from ${data.source === 'api' ? 'Fuel Finder API' : 'cached data'}`);
        } else {
          setApiGradesFetched(false);
          safeToast.error(data.message || 'No grades found. Select manually below.');
        }
      } else {
        safeToast.error('Failed to fetch grades');
        setApiGradesFetched(false);
      }
    } catch (error) { safeToast.error('Failed to fetch grades from API'); setApiGradesFetched(false); }
    finally { setFetchingGrades(false); }
  };

  const sendSiteVerification = async (siteId) => {
    try {
      const response = await fetch(`${API}/admin/sites/resend-verification`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: siteId, code: '' }), credentials: 'include'
      });
      if (response.ok) safeToast.success('Verification email sent');
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail, 'Failed to send')); }
    } catch (error) { safeToast.error('Failed to send verification email'); }
  };

  const verifySiteEmail = async (siteId) => {
    if (!verifyCode) { safeToast.error('Enter verification code'); return; }
    try {
      const response = await fetch(`${API}/admin/sites/verify-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: siteId, code: verifyCode }), credentials: 'include'
      });
      if (response.ok) { safeToast.success('Site email verified!'); setVerifyingSiteEmail(null); setVerifyCode(''); fetchSites(); }
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail, 'Verification failed')); }
    } catch (error) { safeToast.error('Verification failed'); }
  };

  const fetchSiteInfo = async () => {
    if (!form.node_id) { safeToast.error('Enter Site ID first'); return; }
    setFetchingInfo(true);
    try {
      const response = await fetch(`${API}/admin/sites/fetch-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node_id: form.node_id }),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.found) {
          setForm(prev => ({
            ...prev,
            site_name: data.site_name || prev.site_name,
            fuel_grades: data.fuel_grades && data.fuel_grades.length > 0 ? data.fuel_grades : prev.fuel_grades,
          }));
          setApiGradesFetched(data.fuel_grades && data.fuel_grades.length > 0);
          const details = [data.site_name, data.brand_name, data.address, data.city, data.postcode].filter(Boolean).join(', ');
          safeToast.success(`Site found: ${details}`);
        } else {
          safeToast.error(data.message || 'Site not found');
        }
      } else {
        safeToast.error('Failed to fetch site info');
      }
    } catch (error) { safeToast.error('Failed to fetch site info from API'); }
    finally { setFetchingInfo(false); }
  };

  const handleSave = async () => {
    if (!form.site_name || !form.node_id) { safeToast.error('Site name and Node ID are required'); return; }
    if (!form.api_site_id) { safeToast.error('API Site ID is required'); return; }
    if (!form.api_client_id || !form.api_client_secret) { safeToast.error('API Client ID and Client Secret are required'); return; }
    if (!form.site_email) { safeToast.error('Site email is required'); return; }
    if (form.fuel_grades.length === 0) { safeToast.error('Select at least one fuel grade'); return; }
    const minVal = parseFloat(form.price_min);
    const maxVal = parseFloat(form.price_max);
    if (isNaN(minVal) || isNaN(maxVal) || minVal >= maxVal) { safeToast.error('Price range: minimum must be less than maximum'); return; }
    const endDigit = parseInt(form.price_ending_digit, 10);
    if (isNaN(endDigit) || endDigit < 0 || endDigit > 9) { safeToast.error('Price ending digit must be 0-9'); return; }

    try {
      const url = editingSite ? `${API}/admin/sites/${editingSite.site_id}` : `${API}/admin/sites`;
      const method = editingSite ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form), credentials: 'include'
      });
      if (response.ok) {
        safeToast.success(editingSite ? 'Site updated' : 'Site created');
        setShowForm(false);
        setEditingSite(null);
        setForm({ site_name: '', node_id: '', api_site_id: '', api_client_id: '', api_client_secret: '', fuel_grades: [], site_email: '', price_ending_digit: '9', price_min: '100', price_max: '250' });
        fetchSites();
      } else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail, 'Failed to save site')); }
    } catch (error) { safeToast.error('Failed to save site'); }
  };

  const deleteSite = async (siteId) => {
    if (!window.confirm('Delete this site?')) return;
    try {
      const response = await fetch(`${API}/admin/sites/${siteId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { safeToast.success('Site deleted'); fetchSites(); }
    } catch (error) { safeToast.error('Failed to delete site'); }
  };

  const startEdit = (site) => {
    setEditingSite(site);
    setForm({
      site_name: site.site_name,
      node_id: site.node_id,
      api_site_id: site.api_site_id || '',
      api_client_id: site.api_client_id || '',
      api_client_secret: site.api_client_secret || '',
      fuel_grades: site.fuel_grades || [],
      site_email: site.site_email || '',
      price_ending_digit: site.price_ending_digit != null ? String(site.price_ending_digit) : '9',
      price_min: site.price_min != null ? String(site.price_min) : '100',
      price_max: site.price_max != null ? String(site.price_max) : '250'
    });
    setApiGradesFetched(false);
    setShowForm(true);
  };

  const toggleGrade = (gradeId) => {
    setForm(prev => ({
      ...prev,
      fuel_grades: prev.fuel_grades.includes(gradeId)
        ? prev.fuel_grades.filter(g => g !== gradeId)
        : [...prev.fuel_grades, gradeId]
    }));
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {!showForm && (
        <>
          <button data-testid="add-site-btn" onClick={() => { setShowForm(true); setEditingSite(null); setApiGradesFetched(false); setForm({ site_name: '', node_id: '', api_site_id: '', api_client_id: '', api_client_secret: '', fuel_grades: [], site_email: '', price_ending_digit: '9', price_min: '100', price_max: '250' }); }}
            className="btn-primary flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Add Site
          </button>

          {sites.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No sites configured yet.</p>
          ) : (
            <div className="space-y-3">
              {sites.map(site => (
                <div key={site.site_id} data-testid={`site-card-${site.site_id}`}
                  className="p-4 border border-border rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-bold">{site.site_name}</p>
                      <p className="text-sm text-muted-foreground">Node: {site.node_id}</p>
                      {site.api_site_id && <p className="text-sm text-muted-foreground">API Site ID: {site.api_site_id}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(site)} className="p-1 text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteSite(site.site_id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(site.fuel_grades || []).map(g => (
                      <span key={g} className="text-xs px-2 py-1 bg-muted rounded">{g}</span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">API: {site.api_client_id ? 'Configured' : 'Not configured'}</p>
                  {site.site_email && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground">
                        Email: {site.site_email}
                        {site.site_email_verified ? (
                          <span className="ml-2 text-green-600 dark:text-green-400 font-medium">Verified</span>
                        ) : (
                          <span className="ml-2 text-yellow-600 dark:text-yellow-400 font-medium">Unverified</span>
                        )}
                      </p>
                      {!site.site_email_verified && (
                        <div className="mt-1">
                          {verifyingSiteEmail === site.site_id ? (
                            <div className="flex items-center gap-2 mt-1">
                              <input type="text" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)}
                                placeholder="Enter code" maxLength={8}
                                className="search-input text-sm w-32 py-1" data-testid={`verify-code-${site.site_id}`} />
                              <button onClick={() => verifySiteEmail(site.site_id)} data-testid={`verify-site-email-${site.site_id}`}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">Verify</button>
                              <button onClick={() => { setVerifyingSiteEmail(null); setVerifyCode(''); }}
                                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                            </div>
                          ) : (
                            <div className="flex gap-2 mt-1">
                              <button onClick={() => sendSiteVerification(site.site_id)} data-testid={`send-verify-${site.site_id}`}
                                className="text-xs px-2 py-1 bg-brand-navy text-white rounded hover:bg-brand-navy/80">Send Verification</button>
                              <button onClick={() => setVerifyingSiteEmail(site.site_id)} data-testid={`enter-code-${site.site_id}`}
                                className="text-xs px-2 py-1 border border-border rounded hover:bg-muted">Enter Code</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="space-y-4" data-testid="site-form">
          <h3 className="font-heading text-sm font-semibold uppercase">{editingSite ? 'Edit Site' : 'Add New Site'}</h3>
          <div>
            <label className="text-sm font-medium block mb-1">API Site ID (for Price Submission) <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <input data-testid="site-node-input" type="text" value={form.node_id} onChange={(e) => setForm(prev => ({ ...prev, node_id: e.target.value }))}
                className="search-input flex-1" placeholder="e.g. wvx7dskn7a37" />
              <button
                data-testid="fetch-site-info-btn"
                onClick={fetchSiteInfo}
                disabled={fetchingInfo || !form.node_id}
                className="px-4 py-2 bg-brand-navy text-white rounded-sm text-sm hover:bg-brand-navy/80 flex items-center gap-1 disabled:opacity-50 whitespace-nowrap"
              >
                {fetchingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Fetch from API
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Enter the API Site ID and click "Fetch from API" to auto-fill site name and fuel grades</p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Site ID (Fuel Finder Node ID)</label>
            <input data-testid="site-api-site-id-input" type="text" value={form.api_site_id} onChange={(e) => setForm(prev => ({ ...prev, api_site_id: e.target.value }))}
              className="search-input w-full" placeholder="e.g. long node ID from Fuel Finder" />
            <p className="text-xs text-muted-foreground mt-1">The Fuel Finder Node ID. Optional — used for internal reference.</p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Site Name</label>
            <input data-testid="site-name-input" type="text" value={form.site_name} onChange={(e) => setForm(prev => ({ ...prev, site_name: e.target.value }))}
              className="search-input w-full" placeholder="e.g. My Fuel Station" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">API Client ID <span className="text-red-500">*</span></label>
            <input data-testid="site-client-id-input" type="text" value={form.api_client_id} onChange={(e) => setForm(prev => ({ ...prev, api_client_id: e.target.value }))}
              autoComplete="off" name="site_api_client_id" className="search-input w-full" placeholder="Client ID for price submission" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">API Client Secret <span className="text-red-500">*</span></label>
            <input data-testid="site-client-secret-input" type="text" value={form.api_client_secret} onChange={(e) => setForm(prev => ({ ...prev, api_client_secret: e.target.value }))}
              autoComplete="off" name="site_api_client_secret" className="search-input w-full" placeholder="Client Secret for price submission" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Fuel Grades</label>
            {apiGradesFetched && (
              <p className="text-xs text-green-600 dark:text-green-400 mb-2">
                Grades fetched from Fuel Finder API. These match your site's registered grades.
              </p>
            )}
            {!apiGradesFetched && form.fuel_grades.length === 0 && (
              <p className="text-xs text-muted-foreground mb-2">
                Enter Node ID and API credentials above, then click "Fetch Grades" to auto-detect grades from the Fuel Finder API.
              </p>
            )}
            {(apiGradesFetched || form.fuel_grades.length > 0) && (
            <div className="grid grid-cols-2 gap-2">
              {availableGrades.filter(grade => apiGradesFetched ? form.fuel_grades.includes(grade.id) : true).map(grade => (
                <label key={grade.id} className={`flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted transition-colors ${
                  form.fuel_grades.includes(grade.id) ? 'border-brand-red bg-brand-red/5' : 'border-border'
                }`}
                  data-testid={`grade-checkbox-${grade.id}`}>
                  <input type="checkbox" checked={form.fuel_grades.includes(grade.id)} onChange={() => toggleGrade(grade.id)}
                    className="w-4 h-4 accent-brand-red" />
                  <span className="text-sm">{grade.name}</span>
                </label>
              ))}
            </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Site Email <span className="text-red-500">*</span></label>
            <input data-testid="site-email-input" type="email" value={form.site_email} onChange={(e) => setForm(prev => ({ ...prev, site_email: e.target.value }))}
              required className="search-input w-full" placeholder="notifications@example.com" />
            <p className="text-xs text-muted-foreground mt-1">Email address for site notifications. A verification email will be sent after saving.</p>
          </div>
          <div className="border border-border rounded-md p-4 space-y-3">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Price Validation Rules</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Min Price (p)</label>
                <input data-testid="site-price-min" type="number" value={form.price_min} onChange={(e) => setForm(prev => ({ ...prev, price_min: e.target.value }))}
                  className="search-input w-full" placeholder="100" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Max Price (p)</label>
                <input data-testid="site-price-max" type="number" value={form.price_max} onChange={(e) => setForm(prev => ({ ...prev, price_max: e.target.value }))}
                  className="search-input w-full" placeholder="250" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Ending Digit</label>
                <input data-testid="site-price-ending" type="number" min="0" max="9" value={form.price_ending_digit} onChange={(e) => setForm(prev => ({ ...prev, price_ending_digit: e.target.value }))}
                  className="search-input w-full" placeholder="9" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Prices must end in .{form.price_ending_digit || '9'} (e.g. 144.{form.price_ending_digit || '9'}) and be between {form.price_min || '100'}p - {form.price_max || '250'}p</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="save-site-btn" onClick={handleSave} className="btn-primary flex items-center gap-2">
              <Save className="w-4 h-4" /> {editingSite ? 'Update' : 'Create'} Site
            </button>
            <button onClick={() => { setShowForm(false); setEditingSite(null); }}
              className="px-4 py-2 border border-border rounded-sm hover:bg-muted transition-colors">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Admin Email Panel ==============
const AdminEmailPanel = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_email: '', smtp_password: '', from_name: 'PROMORTEK', admin_notification_email: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const isPrimaryAdmin = user?.email?.toLowerCase() === 'vijay@promortek.com';

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/admin/email-config`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.config) setConfig(prev => ({ ...prev, ...data.config, smtp_password: '' }));
      }
    } catch (error) { console.error('Error loading email config'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/email-config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config), credentials: 'include'
      });
      if (response.ok) safeToast.success('Email configuration saved');
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail, 'Failed to save configuration')); }
    } catch (error) { safeToast.error('Failed to save configuration'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await fetch(`${API}/admin/email-config/test`, { method: 'POST', credentials: 'include' });
      if (response.ok) safeToast.success('Test email sent!');
      else { const data = await response.json(); safeToast.error(getErrorMsg(data.detail, 'Failed to send test email')); }
    } catch (error) { safeToast.error('Failed to send test email'); }
    finally { setTesting(false); }
  };

  if (!isPrimaryAdmin) {
    return (
      <div className="text-center py-4">
        <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-muted-foreground">Only vijay@promortek.com can configure email settings.</p>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="email-config-form">
      <p className="text-sm text-muted-foreground">Configure Gmail SMTP to send account approval notifications.</p>
      <div>
        <label className="text-sm font-medium block mb-1">SMTP Host</label>
        <input data-testid="smtp-host" type="text" value={config.smtp_host} onChange={(e) => setConfig(prev => ({ ...prev, smtp_host: e.target.value }))} className="search-input w-full" />
      </div>
      <div>
        <label className="text-sm font-medium block mb-1">SMTP Port</label>
        <input data-testid="smtp-port" type="number" value={config.smtp_port} onChange={(e) => setConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))} className="search-input w-full" />
      </div>
      <div>
        <label className="text-sm font-medium block mb-1">Email Address</label>
        <input data-testid="smtp-email" type="email" value={config.smtp_email} onChange={(e) => setConfig(prev => ({ ...prev, smtp_email: e.target.value }))} className="search-input w-full" placeholder="your@gmail.com" />
      </div>
      <div>
        <label className="text-sm font-medium block mb-1">App Password</label>
        <input data-testid="smtp-password" type="password" value={config.smtp_password} onChange={(e) => setConfig(prev => ({ ...prev, smtp_password: e.target.value }))} className="search-input w-full" placeholder="Gmail App Password" />
        <p className="text-xs text-muted-foreground mt-1">Generate at: Google Account &gt; Security &gt; 2-Step Verification &gt; App Passwords</p>
      </div>
      <div>
        <label className="text-sm font-medium block mb-1">From Name</label>
        <input data-testid="smtp-from-name" type="text" value={config.from_name} onChange={(e) => setConfig(prev => ({ ...prev, from_name: e.target.value }))} className="search-input w-full" />
      </div>
      <div>
        <label className="text-sm font-medium block mb-1">Admin Notification Email</label>
        <input data-testid="admin-notification-email" type="email" value={config.admin_notification_email} onChange={(e) => setConfig(prev => ({ ...prev, admin_notification_email: e.target.value }))} className="search-input w-full" placeholder="vijay@promortek.com" />
        <p className="text-xs text-muted-foreground mt-1">Email address to receive new user registration notifications. Defaults to vijay@promortek.com.</p>
      </div>
      <div className="flex gap-2">
        <button data-testid="save-email-config-btn" onClick={handleSave} disabled={saving}
          className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button data-testid="test-email-btn" onClick={handleTest} disabled={testing}
          className="px-4 py-2 border border-border rounded-sm hover:bg-muted transition-colors flex items-center gap-2">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test
        </button>
      </div>
    </div>
  );
};

// ============== App Router ==============
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/pending" element={<PendingApprovalPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/station/:id" element={<ProtectedRoute><StationDetailPage /></ProtectedRoute>} />
      <Route path="/favourites" element={<ProtectedRoute><FavouritesPage /></ProtectedRoute>} />
      <Route path="/submit-prices" element={<ProtectedRoute requiredRole={['manager', 'admin']}><SubmitPricesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
    </Routes>
  );
}

// ============== Main App ==============
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors toastOptions={{ style: { zIndex: 99999 } }} />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
